import { useEffect, useRef, useState } from 'react'
import { usePaused } from './usePlayback'

const TICK_MS = 100

/**
 * Tracks how much un-paused time has passed since mount.
 */
function useElapsed(): number {
  const paused = usePaused()
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    if (paused) return
    const interval = setInterval(() => {
      setElapsed((prev) => prev + TICK_MS)
    }, TICK_MS)
    return () => clearInterval(interval)
  }, [paused])

  return elapsed
}

/**
 * Fires a sequence of timed "beats" from mount, freezing while paused.
 * Returns how many beats have elapsed (0 before the first offset fires).
 */
export function useBeats(offsets: number[]): number {
  const elapsed = useElapsed()
  return offsets.filter((offset) => elapsed >= offset).length
}

/**
 * A setTimeout that stops counting while the experience is paused.
 */
export function usePausableTimeout(callback: () => void, delay: number): void {
  const elapsed = useElapsed()
  const firedRef = useRef(false)
  const callbackRef = useRef(callback)
  callbackRef.current = callback

  useEffect(() => {
    if (firedRef.current || elapsed < delay) return
    firedRef.current = true
    callbackRef.current()
  }, [elapsed, delay])
}
